import {
  MAX_PALETTE_COLORS,
  colorDistance,
  hexToHsl,
  rgbToHex,
  type DecorPalette,
  type DecorPictureKind,
} from '@peles/shared';

/**
 * The colours in a picture, most seen first — what the event's look is taken
 * from when a producer adds pictures of the chairs, the flowers, the hall or
 * the tableware (`DecorPalette`).
 *
 * Read in the browser, from a small copy of each picture: free, instant, and
 * nothing leaves the page. The AI reads the styles; this reads the colours.
 */

/** A picture's pixels, RGBA, row by row. */
export interface Pixels {
  data: Uint8ClampedArray;
  width: number;
  height: number;
}

/** Part of a picture, as fractions of its width and height. */
export interface Region {
  x0: number;
  y0: number;
  x1: number;
  y1: number;
}

export interface PaletteOptions {
  /** Read only this part of the picture. */
  region?: Region;
  /** Count the middle more than the edges — the subject, not the backdrop. */
  centre?: boolean;
  /** Count coloured pixels more than grey ones — flowers over the table under them. */
  favourColour?: boolean;
  /** Colours closer than this are one colour. */
  mergeDistance?: number;
}

export interface WeightedColor {
  color: string;
  weight: number;
}

/** Bins of 4 bits a channel: 4,096 colours, enough to tell blush from rose. */
const BITS = 4;
const SHIFT = 8 - BITS;

/**
 * The colours of a picture, most seen first, with how much of it each covers
 * (the weights add up to 1). Transparent pixels are not counted.
 */
export function weightedColors(pixels: Pixels, options: PaletteOptions = {}): WeightedColor[] {
  const { data, width, height } = pixels;
  const region = options.region ?? { x0: 0, y0: 0, x1: 1, y1: 1 };
  const xs = Math.floor(region.x0 * width);
  const xe = Math.ceil(region.x1 * width);
  const ys = Math.floor(region.y0 * height);
  const ye = Math.ceil(region.y1 * height);
  const bins = new Map<number, { w: number; r: number; g: number; b: number }>();
  let total = 0;
  for (let y = ys; y < ye; y++) {
    for (let x = xs; x < xe; x++) {
      const i = (y * width + x) * 4;
      if (data[i + 3] < 128) continue;
      const r = data[i];
      const g = data[i + 1];
      const b = data[i + 2];
      let w = 1;
      if (options.centre) {
        const dx = (x + 0.5) / width - 0.5;
        const dy = (y + 0.5) / height - 0.5;
        w *= Math.exp(-(dx * dx + dy * dy) / 0.08);
      }
      if (options.favourColour) {
        const max = Math.max(r, g, b);
        const min = Math.min(r, g, b);
        w *= 0.25 + (max === 0 ? 0 : (max - min) / max) * 2;
      }
      const key = ((r >> SHIFT) << (2 * BITS)) | ((g >> SHIFT) << BITS) | (b >> SHIFT);
      const bin = bins.get(key) ?? { w: 0, r: 0, g: 0, b: 0 };
      bin.w += w;
      bin.r += r * w;
      bin.g += g * w;
      bin.b += b * w;
      bins.set(key, bin);
      total += w;
    }
  }
  if (total === 0) return [];
  // Each bin's average colour, then near neighbours merged into the heavier.
  const sorted = [...bins.values()]
    .filter((b) => b.w > 0)
    .sort((a, b) => b.w - a.w)
    .map((b) => ({ color: rgbToHex({ r: b.r / b.w, g: b.g / b.w, b: b.b / b.w }), weight: b.w }));
  const merge = options.mergeDistance ?? 28;
  const merged: WeightedColor[] = [];
  for (const c of sorted) {
    const near = merged.find((m) => colorDistance(m.color, c.color) < merge);
    if (near) near.weight += c.weight;
    else merged.push({ ...c });
  }
  return merged
    .map((m) => ({ color: m.color, weight: m.weight / total }))
    .sort((a, b) => b.weight - a.weight);
}

/**
 * Several pictures' colours as one list: each picture counts the same however
 * large it is, and a colour that covers almost nothing is left out.
 */
export function combineColors(
  lists: WeightedColor[][],
  max = MAX_PALETTE_COLORS,
  minShare = 0.02,
  mergeDistance = 28,
): string[] {
  const pooled: WeightedColor[] = [];
  const share = lists.length ? 1 / lists.length : 0;
  for (const list of lists) {
    for (const c of list) {
      const near = pooled.find((p) => colorDistance(p.color, c.color) < mergeDistance);
      if (near) near.weight += c.weight * share;
      else pooled.push({ color: c.color, weight: c.weight * share });
    }
  }
  return pooled
    .filter((c) => c.weight >= minShare)
    .sort((a, b) => b.weight - a.weight)
    .slice(0, max)
    .map((c) => c.color);
}

/** The single colour most of a region is — a hall's walls or its floor. */
function dominant(pictures: Pixels[], region: Region): string | undefined {
  return combineColors(
    pictures.map((p) => weightedColors(p, { region, mergeDistance: 40 })),
    1,
    0,
    40,
  )[0];
}

/** The ceiling and lights take the very top; the walls sit below them. */
const WALL_BAND: Region = { x0: 0, y0: 0.12, x1: 1, y1: 0.45 };
/** The floor is what the camera looks down on at the bottom of a room photo. */
const FLOOR_BAND: Region = { x0: 0.1, y0: 0.8, x1: 0.9, y1: 1 };

/** What one group of pictures adds to the palette. */
export function paletteFor(kind: DecorPictureKind, pictures: Pixels[]): DecorPalette {
  if (pictures.length === 0) return {};
  switch (kind) {
    case 'chairs':
      return { chairs: combineColors(pictures.map((p) => weightedColors(p, { centre: true }))) };
    case 'flowers':
      return {
        flowers: combineColors(
          pictures.map((p) => weightedColors(p, { centre: true, favourColour: true })),
        ),
      };
    case 'tableware':
      return { tableware: combineColors(pictures.map((p) => weightedColors(p))) };
    case 'hall': {
      const wall = dominant(pictures, WALL_BAND);
      const floor = dominant(pictures, FLOOR_BAND);
      return { ...(wall ? { hallWall: wall } : {}), ...(floor ? { hallFloor: floor } : {}) };
    }
  }
}

/** A palette with one group's colours replaced — the others stay as they were. */
export function withGroupPalette(
  palette: DecorPalette,
  kind: DecorPictureKind,
  group: DecorPalette,
): DecorPalette {
  const next: DecorPalette = { ...palette };
  if (kind === 'hall') {
    delete next.hallWall;
    delete next.hallFloor;
  } else {
    delete next[kind];
  }
  return { ...next, ...group };
}

/** The colours worth offering as one-press choices: the palette, de-duplicated. */
export function swatchesOf(palette: DecorPalette): string[] {
  const all = [
    ...(palette.chairs ?? []),
    ...(palette.flowers ?? []),
    ...(palette.tableware ?? []),
    ...(palette.hallWall ? [palette.hallWall] : []),
    ...(palette.hallFloor ? [palette.hallFloor] : []),
  ];
  const kept: string[] = [];
  all.forEach((c) => {
    if (!kept.some((k) => colorDistance(k, c) < 18)) kept.push(c);
  });
  // Light to dark, so the row reads as a strip rather than noise.
  return kept.sort((a, b) => hexToHsl(b).l - hexToHsl(a).l);
}

/** How large a copy of a picture is read — plenty to find its colours. */
export const PALETTE_SAMPLE_EDGE = 96;

/**
 * A small copy of a picture's pixels, from a file just chosen or a stored
 * picture's address. Null where it cannot be drawn (no canvas, a picture the
 * browser cannot decode, an address that refuses).
 */
export async function loadPixels(source: Blob | string): Promise<Pixels | null> {
  try {
    let blob: Blob;
    if (typeof source === 'string') {
      const response = await fetch(source, { mode: 'cors' });
      if (!response.ok) return null;
      blob = await response.blob();
    } else {
      blob = source;
    }
    if (typeof createImageBitmap !== 'function') return null;
    const bitmap = await createImageBitmap(blob);
    const ratio = Math.min(1, PALETTE_SAMPLE_EDGE / Math.max(bitmap.width, bitmap.height));
    const width = Math.max(1, Math.round(bitmap.width * ratio));
    const height = Math.max(1, Math.round(bitmap.height * ratio));
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const context = canvas.getContext('2d');
    if (!context) return null;
    context.drawImage(bitmap, 0, 0, width, height);
    bitmap.close?.();
    const { data } = context.getImageData(0, 0, width, height);
    return { data, width, height };
  } catch {
    return null;
  }
}
